import type { AnalyticsEventName, AnalyticsEventPayload } from "./events";

type CaptureFn = (params: {
  event: string;
  properties: Record<string, unknown>;
  distinctId?: string;
}) => Promise<void> | void;

type IdentifyFn = (
  distinctId: string,
  properties?: Record<string, unknown>,
) => Promise<void> | void;

type RegisterPropertiesFn = (properties: Record<string, unknown>) => void;

type ClientOptions = {
  capture: CaptureFn;
  identify?: IdentifyFn;
  registerProperties?: RegisterPropertiesFn;
  enabled?: boolean;
  onError?: (error: unknown) => void;
};

export type AnalyticsClient = {
  capture: <Name extends AnalyticsEventName>(
    name: Name,
    payload: AnalyticsEventPayload<Name>,
  ) => Promise<void>;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  setGlobalProperties: (props: Record<string, unknown>) => void;
  withGlobalProperties: (props: Record<string, unknown>) => AnalyticsClient;
  getGlobalProperties: () => Record<string, unknown>;
};

const noopClient: AnalyticsClient = {
  capture: async () => {},
  identify: () => {},
  setGlobalProperties: () => {},
  withGlobalProperties: () => noopClient,
  getGlobalProperties: () => ({}),
};

export function createAnalyticsClient(
  options: ClientOptions,
  initialProperties: Record<string, unknown> = {},
): AnalyticsClient {
  if (!options.enabled) {
    return noopClient;
  }

  let globalProperties = { ...initialProperties };
  let currentDistinctId: string | undefined;

  const identify = (distinctId: string, properties?: Record<string, unknown>) =>
    options.identify?.(distinctId, properties) ?? (currentDistinctId = distinctId);

  const setGlobalProperties = (props: Record<string, unknown>) => {
    globalProperties = {
      ...globalProperties,
      ...props,
    };
    options.registerProperties?.(globalProperties);
  };

  const capture = async <Name extends AnalyticsEventName>(
    name: Name,
    payload: AnalyticsEventPayload<Name>,
  ) => {
    const merged = { ...globalProperties, ...payload };
    try {
      await options.capture({
        event: name,
        properties: merged,
        distinctId: currentDistinctId,
      });
    } catch (error) {
      options.onError?.(error);
    }
  };

  const withGlobalProperties = (props: Record<string, unknown>) =>
    createAnalyticsClient(options, { ...globalProperties, ...props });

  return {
    capture,
    setGlobalProperties,
    withGlobalProperties,
    getGlobalProperties: () => globalProperties,
    identify,
  };
}
